// =============================================================================
// NS Small B2B - RBAC (Role-Based Access Control)
// =============================================================================

import type { Employee, UserRole, Visit, Site, Followup } from './types';

// -----------------------------------------------------------------------------
// Role Levels
// -----------------------------------------------------------------------------

export const ROLE_LEVELS: Record<UserRole, number> = {
    ADMIN: 4,
    EXECUTIVE: 3,
    MANAGER: 2,
    EMPLOYEE: 1,
};

// -----------------------------------------------------------------------------
// Role Checks
// -----------------------------------------------------------------------------

export function isAdmin(role: UserRole): boolean {
    return role === 'ADMIN';
}

export function isExecutive(role: UserRole): boolean {
    return role === 'EXECUTIVE';
}

export function isAdminLike(role: UserRole): boolean {
    return role === 'ADMIN' || role === 'EXECUTIVE';
}

export function isManager(role: UserRole): boolean {
    return role === 'MANAGER';
}

export function isManagerPlus(role: UserRole): boolean {
    return ROLE_LEVELS[role] >= ROLE_LEVELS.MANAGER;
}

export function isEmployee(role: UserRole): boolean {
    return role === 'EMPLOYEE';
}

// -----------------------------------------------------------------------------
// Super Admin Check
// -----------------------------------------------------------------------------

export function isSuperAdmin(userId: string, superAdminIds: string): boolean {
    if (!superAdminIds) return false;
    const ids = superAdminIds.split(',').map((id) => id.trim());
    return ids.includes(userId);
}

// -----------------------------------------------------------------------------
// Get Effective Role
// -----------------------------------------------------------------------------

export interface GetEffectiveRoleOptions {
    userId: string;
    employee: Employee | null;
    superAdminIds: string;
    devRole?: UserRole;
}

export function getEffectiveRole(options: GetEffectiveRoleOptions): UserRole {
    const { userId, employee, superAdminIds, devRole } = options;

    // 1. Super admin always ADMIN
    if (isSuperAdmin(userId, superAdminIds)) {
        return 'ADMIN';
    }

    // 2. Dev mode override
    if (devRole) {
        return devRole;
    }

    if (!employee) {
        return 'EMPLOYEE';
    }

    // 3. Explicit role column
    if (employee.role) {
        return employee.role;
    }

    // 4. Legacy flags
    if (employee.is_admin === 1) {
        return 'ADMIN';
    }
    if (employee.is_manager === 1) {
        return 'MANAGER';
    }

    // 5. Default
    return 'EMPLOYEE';
}

// -----------------------------------------------------------------------------
// Visit Access Rules
// -----------------------------------------------------------------------------

export interface VisitAccessContext {
    userId: string;
    role: UserRole;
    team: string;
}

export function canViewVisit(user: VisitAccessContext, visit: Visit): boolean {
    // ADMIN/EXECUTIVE can view all
    if (isAdminLike(user.role)) {
        return true;
    }

    // MANAGER can view own team
    if (isManager(user.role)) {
        return visit.team === user.team;
    }

    // EMPLOYEE can view own + team
    return visit.created_by === user.userId || visit.team === user.team;
}

export function canModifyVisit(user: VisitAccessContext, visit: Visit): boolean {
    // ADMIN/EXECUTIVE can modify all
    if (isAdminLike(user.role)) {
        return true;
    }

    // MANAGER can modify own team
    if (isManager(user.role)) {
        return visit.team === user.team;
    }

    // EMPLOYEE can only modify own
    return visit.created_by === user.userId;
}

// -----------------------------------------------------------------------------
// Site Access Rules
// -----------------------------------------------------------------------------

export function canViewSite(user: VisitAccessContext, site: Site): boolean {
    // Sites require MANAGER+ to access
    if (!isManagerPlus(user.role)) {
        return false;
    }

    // ADMIN/EXECUTIVE can view all
    if (isAdminLike(user.role)) {
        return true;
    }

    // MANAGER can only view own team
    return site.team === user.team;
}

// -----------------------------------------------------------------------------
// Followup Access Rules
// -----------------------------------------------------------------------------

export function canViewFollowup(user: VisitAccessContext, followup: Followup): boolean {
    // Followups require MANAGER+ to access
    if (!isManagerPlus(user.role)) {
        return false;
    }

    // ADMIN/EXECUTIVE can view all
    if (isAdminLike(user.role)) {
        return true;
    }

    // MANAGER can only view own team
    return followup.team === user.team;
}

export function canCreateFollowup(user: VisitAccessContext): boolean {
    return isManagerPlus(user.role);
}

// -----------------------------------------------------------------------------
// Employee Access Rules
// -----------------------------------------------------------------------------

export function canViewEmployee(
    user: VisitAccessContext,
    targetEmployee: Employee
): boolean {
    // ADMIN/EXECUTIVE can view all
    if (isAdminLike(user.role)) {
        return true;
    }

    // MANAGER can view own team
    if (isManager(user.role)) {
        return targetEmployee.team === user.team;
    }

    // EMPLOYEE can only view self
    return targetEmployee.id === user.userId;
}

export function canModifyEmployee(
    user: VisitAccessContext,
    targetEmployee: Employee,
    superAdminIds: string
): boolean {
    // Cannot modify super admin unless you are super admin
    if (isSuperAdmin(targetEmployee.id, superAdminIds)) {
        return isSuperAdmin(user.userId, superAdminIds);
    }

    // ADMIN/EXECUTIVE can modify all
    if (isAdminLike(user.role)) {
        return true;
    }

    // MANAGER can modify own team
    if (isManager(user.role)) {
        return targetEmployee.team === user.team;
    }

    return false;
}

export function canDeleteEmployee(user: VisitAccessContext): boolean {
    return isAdminLike(user.role);
}

// -----------------------------------------------------------------------------
// Endpoint RBAC Guards
// -----------------------------------------------------------------------------

export type EndpointRole = 'ADMIN' | 'ADMIN/EXEC' | 'MANAGER+' | 'ACTIVE' | 'PUBLIC';

export function checkEndpointAccess(
    userRole: UserRole,
    userStatus: string,
    requiredRole: EndpointRole
): boolean {
    // PUBLIC endpoints always accessible
    if (requiredRole === 'PUBLIC') {
        return true;
    }

    // All other endpoints require active status
    if (userStatus !== 'active') {
        return false;
    }

    switch (requiredRole) {
        case 'ADMIN':
            return isAdmin(userRole);

        case 'ADMIN/EXEC':
            return isAdminLike(userRole);

        case 'MANAGER+':
            return isManagerPlus(userRole);

        case 'ACTIVE':
            return true; // Already checked status above

        default:
            return false;
    }
}

// -----------------------------------------------------------------------------
// Map Redaction
// -----------------------------------------------------------------------------

export interface MapVisitRedacted {
    id: string;
    lat: number;
    lng: number;
    team: string;
    date: string;
    customer_type?: string;
    distance_km?: number;
    created_by_name?: string;
    // Sensitive fields omitted
}

export interface MapVisitFull extends MapVisitRedacted {
    customer_name: string;
    phone: string;
    jobsite: string | null;
    notes: string | null;
    rep: string | null;
    product_groups: string | null;
    estimate: string | null;
    shop: string | null;
}

export function redactVisitForMap(
    visit: MapVisitFull,
    userTeam: string
): MapVisitRedacted | MapVisitFull {
    // Same team: return full data
    if (visit.team === userTeam) {
        return visit;
    }

    // Different team: redact sensitive fields
    return {
        id: visit.id,
        lat: visit.lat,
        lng: visit.lng,
        team: visit.team,
        date: visit.date,
        customer_type: visit.customer_type,
        distance_km: visit.distance_km,
        created_by_name: visit.created_by_name,
    };
}
